import React from 'react'
export interface DashboardItem {
  title: string
  anchorText: string
  route: ({ isInternal }: { isInternal?: boolean }) => string
  show: boolean
  description: React.ReactNode
  showExternal: boolean
  isExternal?: boolean
}
const DashboardConfig: DashboardItem[] = [
  {
    title: 'Product and material samples',
    description: (
      <>
        <p>
          Understand the scope of product and material samples managed in Spark,
          including:
        </p>
        <ul>
          <li>Number of samples requested</li>
          <li>Past due and upcoming samples</li>
          <li>Sample breakout by evaluation status</li>
          <li>Types of samples requested</li>
          <li>Trends over time</li>
        </ul>
      </>
    ),
    anchorText: 'View samples insights',
    route: () => 'samples',
    show: true,
    showExternal: true,
  },
  {
    title: 'Claims',
    description: (
      <>
        <p>
          Understand the scope of claims associated to products in Spark,
          including:
        </p>
        <ul>
          <li>Products associated to claim(s)</li>
          <li>Claim substantiation by business partner</li>
          <li>Most frequently used claims</li>
          <li>Trends over time</li>
        </ul>
      </>
    ),
    anchorText: 'View claims insights',
    route: () => 'claims',
    show: true,
    showExternal: false,
  },
  {
    title: 'Accredited partners',
    description: (
      <>
        <p>
          Understand the performance and scope of accredited partners in sample
          management, including:
        </p>
        <ul>
          <li>Sample breakout by evaluation status</li>
          <li>Approval rates by round </li>
          <li>Comparison of business partners </li>
          <li>Trends over time</li>
        </ul>
      </>
    ),
    anchorText: 'View accredited partners insights',
    route: () => 'accreditedPartners',
    show: true,
    showExternal: false,
  },
  {
    title: 'Fabric development',
    description: (
      <>
        <p>
          Understand the scope of fabric development and the adoption into
          products.
        </p>
        <ul>
          <li>Fabric development ratio & adoption</li>
          <li> Development by engineer and role</li>
        </ul>
      </>
    ),
    anchorText: 'View fabric development insights',
    route: () => 'fabric-development',
    show: true,
    showExternal: false,
  },
  {
    title: 'Material development',
    description: (
      <>
        <p>
          Understand the scope of material development and the adoption into
          products.
        </p>
        <ul>
          <li>Material development ratio</li>
          <li>Adoption into combinations</li>
          <li>Adoption into products</li>
        </ul>
      </>
    ),
    anchorText: 'View materials insights',
    route: () => 'materials',
    show: true,
    showExternal: false,
  },
  {
    title: '3D development',
    description: (
      <>
        <p>
          Understand the performance and scope of 3D development through sample
          management, including:
        </p>
        <ul>
          <li>Utilization by business</li>
          <li>Comparison of business partners</li>
          <li>Samples by evaluation status and approval round</li>
          <li>Trends over time</li>
        </ul>
      </>
    ),
    anchorText: 'View 3D development insights',
    route: () => 'threeD',
    show: true,
    showExternal: true,
  },
  {
    title: 'Workload',
    description: (
      <>
        <p>
          Understand the workload associated to developing a product within
          Spark, including:
        </p>
        <ul>
          <li>Products developed and managed</li>
          <li>Development ratio for product & sample</li>
          <li>Samples requested and evaluated by user role and user</li>
        </ul>
      </>
    ),
    anchorText: 'View workload insights',
    route: () => 'workload',
    show: true,
    showExternal: false,
  },
  {
    title: 'Bill of materials',
    description: (
      <>
        <p>
          Understand the scope and depth digital and connected bill of materials
          adoption across businesses and business partners.
        </p>
        <ul>
          <li>% of products with an active bill of material</li>
          <li>
            % of products with at least one connected material library asset
          </li>
          <li>Depth of connectivity by business and material type</li>
        </ul>
      </>
    ),
    anchorText: 'View bill of materials insights',
    route: () => 'connectedBom/Product',
    show: true,
    showExternal: true,
  },
  {
    title: 'Packaging',
    description: (
      <>
        <p>Understand the scope of packaging component in Spark</p>
        <ul>
          <li>Number of dielines requested</li>
          <li>Past due and upcoming dielines</li>
          <li>
            Quick view and access to dielines ready to be evaluated and by whom
          </li>
          <li>Dieline evaluations by status</li>
          <li>Failed dielines by business partner</li>
        </ul>
      </>
    ),
    anchorText: 'View packaging insights',
    route: () => 'packaging',
    show: true,
    showExternal: false,
  },
  {
    title: 'Build your own insights (BYO)',
    description: (
      <>
        <p>Grants access to raw data generated in Spark.</p>
        <ul>
          <li>Understand and answer ad hoc questions</li>
          <li>Data playground to automate current manual reporting</li>
        </ul>
      </>
    ),
    anchorText: 'View BYO insights',
    route: () => 'byo',
    show: true,
    showExternal: false,
  },
  {
    title: 'Spark health check',
    description: (
      <>
        <p>
          Understand the adoption or utilization of Spark capabilities as it
          relates to non-negotiables
        </p>
        <ul>
          <li>% of products with specifications component</li>
          <li>% of products with a product watchlist</li>
        </ul>
      </>
    ),
    anchorText: 'View Spark health check insights',
    route: () => 'health-Check',
    show: true,
    showExternal: false,
  },
  {
    title: 'Complete bill of materials',
    description: (
      <>
        <p>
          Understand the scope and depth of connected bill of materials adoption
          across businesses and business partners.
        </p>
        <ul>
          <li>% of ** products have an active BOM</li>
          <li>% of product BOMs in accepted status</li>
          <li>% of product BOM rows with a connected material asset</li>
          <li>% of product BOM rows with a weight/weight UOM</li>
        </ul>
      </>
    ),
    anchorText: 'View Superset BOM dashboard',
    route: ({ isInternal }) =>
      isInternal
        ? `reporting/app/api/auth/plm_superset_login?authDomain=ENTERPRISE`
        : 'reporting/app/api/auth/plm_superset_login?authDomain=POL',
    show: true,
    showExternal: false,
    isExternal: true,
  },
  {
    title: 'PLM operations reporting (beta)',
    description: (
      <>
        <p>
          The future operational reporting home for PLM, powered by Superset
        </p>
        <ul>
          <li>
            Currently in progress with dashboards being released incrementally
          </li>
          <li>
            All existing Spark insights reports will be migrated to the new
            reporting experience over time
          </li>
          <li>
            A new reporting landing page is planned once the migration is
            complete (target: end of Q3 '26)
          </li>
        </ul>
      </>
    ),
    anchorText: 'View PLM reporting (Superset)',
    route: ({ isInternal }) =>
      isInternal
        ? `reporting/app/api/auth/plm_superset_login?authDomain=ENTERPRISE`
        : 'reporting/app/api/auth/plm_superset_login?authDomain=POL',
    show: true,
    showExternal: false,
    isExternal: true,
  },
]

export default DashboardConfig
